// packingStockStatus — single source of truth for classifying a packing material's
// on-hand stock against its configured safe level, and the color it renders in.
// Shared by Settings → Packing Materials (editable) and Inventory → Packing Materials
// (read-only) so the two views can never disagree about what "low" means.
//
// WCO-2155: safe_pieces is resolved by the BACKEND (@tnb/packing-consumption's
// PIECES_PER_BUNDLE_SQL). Never re-derive it here from ics.inventory.packing — a null
// safe_pieces means the item's pieces-per-bundle is unresolvable and must render as
// 'unknown', not as a fabricated comparison against 1 piece per bundle.

// ⚠️ These arrive from the API as STRINGS — Postgres serializes numeric/decimal columns
// as strings, so current_stock is "195.00", not 195. Comparing two of them directly is a
// LEXICOGRAPHIC comparison: "9.00" <= "100.0000" is false, so 9 pieces against a safe level
// of 100 rendered as green "ok". Every comparison below must coerce with Number() first.
export interface PackingStockRow {
  safe_quantity: number | string;
  /** safe_quantity x pieces_per_bundle, resolved server-side. Null = unresolvable. */
  safe_pieces: number | string | null;
  current_stock: number | string;
}

export type PackingStockStatus = 'ok' | 'low' | 'critical' | 'none' | 'unknown';

export const getPackingStockStatus = (row: PackingStockRow): PackingStockStatus => {
  if (Number(row.safe_quantity) <= 0) return 'none';
  if (row.safe_pieces == null) return 'unknown';
  const stock = Number(row.current_stock);
  if (stock <= 0) return 'critical';
  if (stock <= Number(row.safe_pieces)) return 'low';
  return 'ok';
};

export const packingStockStatusColors: Record<PackingStockStatus, string> = {
  ok: 'text-green-600 dark:text-green-400',
  low: 'text-amber-600 dark:text-amber-400',
  critical: 'text-red-600 dark:text-red-400',
  none: 'text-secondary-500 dark:text-gray-400',
  unknown: 'text-secondary-500 dark:text-gray-400',
};
